// What the Engineers page shows about an engineer's notifications (Session PN1): a quiet line in the status cell (on with the number
// of phones, off, or never set up) and one secondary control, "Send a test notification". No new page and no menu item: it lives where
// the office already looks at each engineer.
import { useState } from "react";
import { ApiError, api } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import type { EngineerListItem } from "../../lib/api";

export function notificationsLine(n: EngineerListItem["notifications"]): { text: string; warn: boolean } | null {
  if (!n) return null;
  if (n.push === "on") return { text: `Notifications on (${n.phones} ${n.phones === 1 ? "phone" : "phones"})`, warn: false };
  const text = n.push === "off" ? "Notifications off" : "Notifications not set up";
  return { text: n.textFallback ? `${text}, will text` : text, warn: !n.textFallback };
}

export function NotificationsLine({ engineer }: { engineer: EngineerListItem }) {
  if (!engineer.isActive) return null;
  const line = notificationsLine(engineer.notifications);
  if (!line) return null;
  return (
    <p className={`mt-1 text-xs ${line.warn ? "text-amber-400" : "text-ele-body"}`} data-testid="engineer-notifications-line">
      {line.text}
    </p>
  );
}

// One press sends a generic test (a push to their phones, then one text if none was reached). The result says which, in words.
export function TestNotificationButton({ engineer }: { engineer: EngineerListItem }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ text: string; ok: boolean } | null>(null);
  if (!engineer.isActive) return null;

  async function send() {
    if (busy) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.engineers.testNotification(engineer.id, false);
      setResult({ text: r.message ?? (r.told ? "Test notification sent." : "The test did not reach the engineer."), ok: r.told === true });
    } catch (err) {
      setResult({ text: err instanceof ApiError ? "The test could not be sent. Try again." : "The test could not be sent.", ok: false });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* A quiet secondary action. Each press sends a live push and may send a billable text, so it is the platform's two-step branded confirm and
          the confirm names the engineer (the server also refuses a second test inside a minute). */}
      <ConfirmButton
        onConfirm={() => void send()}
        label="Send a test notification"
        confirmLabel={`Send a test to ${engineer.displayName}?`}
        busy={busy}
        busyLabel="Sending..."
        className="rounded-ele px-2.5 py-1 text-xs text-ele-body transition hover:text-ele-strong disabled:opacity-50"
        armedClassName="!text-ele-strong font-semibold"
      />
      {result ? (
        <p role="status" className={`basis-full text-right text-xs ${result.ok ? "text-ele-body" : "text-amber-400"}`}>
          {result.text}
        </p>
      ) : null}
    </>
  );
}
